/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useState } from "react";
import { ApolloError } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { Role } from "~/generated/graphql";
import { GRAPHQL_ENDPOINT, MOCK_CUSTOMER_ID } from "~/config";
import { http } from "~/services/api";
import { isInputError } from "~/tools";

const CUSTOMERS_ENDPOINT =
  process.env.REACT_APP_CUSTOMERS_ENDPOINT ||
  GRAPHQL_ENDPOINT.replace("graphql", "customers");

export type Customer = {
  id: string;
  name: string;
  role: Role;
  organizationName: string;
};

type CustomerResponse = ApolloError | Customer[];

const ERROR_MESSAGE = "Failed fetching Customers";

const CUSTOMERS_MOCK = [
  {
    id: MOCK_CUSTOMER_ID,
    name: "MOCK_CUSTOMER",
    role: Role.Admin,
    organizationName: "",
  },
];

const isLocalApi =
  CUSTOMERS_ENDPOINT.includes("localhost") ||
  CUSTOMERS_ENDPOINT.includes("0.0.0.0");

async function getCustomers(token: string) {
  try {
    const response = isLocalApi
      ? CUSTOMERS_MOCK
      : await http<CustomerResponse>(CUSTOMERS_ENDPOINT, {
          headers: { Authorization: token },
        });

    // handle case when graphql returns error
    if (isInputError(response)) {
      throw new Error(response.message);
    } else {
      return response;
    }
  } catch {
    throw new Error(ERROR_MESSAGE);
  }
}

export function useFetchCustomers() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);
      try {
        const { id_token } = await getAccessTokenSilently({
          detailedResponse: true,
        });
        const customersResponse = await getCustomers(id_token);

        // handle case when graphql returns error
        if (isInputError(customersResponse)) {
          throw new Error(customersResponse.message);
        } else {
          setCustomers(customersResponse);
        }
      } catch (error) {
        if (isInputError(error)) {
          setErrorMessage(error.message);
        }
      } finally {
        setIsLoading(false);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    getData();
  }, [getAccessTokenSilently]);

  return {
    customers,
    loading: isLoading,
    error: errorMessage,
  };
}
